import { MapCenter } from './map.service';
import { Observable, empty, combineLatest, fromEvent } from 'rxjs';
import { Injectable } from "@angular/core";
import { map } from 'rxjs/operators';

export class GyroUpdate {
    public hdgAbsolute: number = 0;
    public hdgEquivalent: number = 0;
}

@Injectable({ providedIn: 'root' })
export class GyroService {
    public deviceType = "Unknown";
    private initialized = false;
    public permissionsInitialized = false;

    constructor() {
        // Avoid referencing window in SSR (prerender)
        if(typeof window == 'undefined') return;

        // Expose GyroService instance to Cypress e2e test
        (window as any).GyroService = this;
    }

    public init() {
        if(this.initialized) return;
        this.initialized = true;
        // Dont init gyro permissions yet, as this requires a user gesture unless (when are on ios)
        // Just mark permissions ok if on other devices than iOS
        if(typeof DeviceMotionEvent == 'undefined' || !('requestPermission' in DeviceMotionEvent)) this.permissionsInitialized = true;
    }

    /**
    * Watch updates from gyro compas
    * @param pollInterval Time in ms to poll for gyro updates. If 0 or null is given updates
    * will be requested on animFrame.
    */
    public watchGyro(): Observable<GyroUpdate> {
        let lastHdgEquivalent = 0;
        let update = new GyroUpdate();
        let gyroObs = this.getOrientationObserver();

        if(!gyroObs) {
            console.warn("Gyro orientation is not supported on device");
            //err = "Device orientation is not supported";
            return empty();
        }
        return gyroObs.pipe(map((ev: DeviceOrientationEvent) => {
            // Update the gyro update object
            update.hdgEquivalent = 0;
            update.hdgAbsolute = ev.alpha;
            return update;
        }));
    }

    /**
    * Watch updates from gyro compass with map heading changes added (angle relative to map)
    */
    public watchGyroMapHeading(mapService: { watchMapCenter(): Observable<MapCenter> }): Observable<number> {
        let mapGyroHeading = combineLatest(mapService.watchMapCenter(), this.watchGyro());

        return mapGyroHeading.pipe(map(([centerUpdate, gyroUpdate]) => {
            // Calculate user orientation from map rotation and gyro device rotation
            return ((gyroUpdate.hdgAbsolute + (360 - centerUpdate.bearing)) % 360);
        }));
        //.pipe(distinctUntilChanged( (last, next) => last == next ), tap( (heading) => console.log("Map adjusted: " + heading)));
    }

    /**
     * Request for gyro permissions, this is needed on iPhone iOS > 13
     * It must be called base on a user gesture
     */
    public async requestPermissions(): Promise<boolean> {
        if(this.permissionsInitialized) return;

        // If we are not on iOS
        if(typeof DeviceMotionEvent == 'undefined' || !('requestPermission' in DeviceMotionEvent)) {
            // We dont need permissions on this device so mark it ok 
            this.permissionsInitialized = true;
            return this.permissionsInitialized;
        }
        // We are on iOS 13+ if requestPermission exists
        let rsp = await (DeviceMotionEvent as any).requestPermission().catch(err => console.error);

        this.permissionsInitialized = (rsp == 'granted') ? true : false;
        return this.permissionsInitialized;
    }

    private getOrientationObserver(): Observable<DeviceOrientationEvent> | null {
        let obs: Observable<DeviceOrientationEvent> = null;

        // Get absolute device on android devices
        if('ondeviceorientationabsolute' in window) {
            obs = this.getGyroDev('deviceorientationabsolute');
            this.deviceType = "Absolute Gyro Device";
        }
        else if('ondeviceorientation' in window) {
            obs = this.getGyroDev('deviceorientation');
            this.deviceType = "Apple Gyro Device";
        }
        else {
            console.log("OBS: No gyro device detected");
        }

        return obs;
    }

    public getGyroDev(deviceListener: string = "deviceorientationabsolute"): Observable<DeviceOrientationEvent> {
        // Create new orientation event listner
        let gyroDevice = fromEvent<DeviceOrientationEvent>(window, deviceListener);

        return gyroDevice.pipe(map((ev: DeviceOrientationEvent) => {
            // If we get a gyro event, mark permissions ok (for iOS when user has already given permission)
            this.permissionsInitialized = true;

            if(event && (event as any).webkitCompassHeading) {
                // Safari heading
                let heading = (event as any).webkitCompassHeading; //iOS non-standard
                // Create a new DeviceOrientationEvent (hack)
                return ({ alpha: heading, beta: ev.beta, gamma: ev.gamma, absolute: true } as DeviceOrientationEvent);
            }
            else {
                // Save last event in poll wrapper (mirror alpha to be consistent with iOS)
                return ({ alpha: (360 - ev.alpha), beta: ev.beta, gamma: ev.gamma, absolute: true } as DeviceOrientationEvent);
            }
        }));
    }

    // private lastAng = 0;
    public closestEquivalentAngle(fromAng: number, toAng: number): number {
        // Find delta, add 540 (360*1.5) to make sure result is always between -180 and 180, 
        // reduce with modulus 360 and minus 180 (to compensate for 540)
        // https://stackoverflow.com/questions/19618745/css3-rotate-transition-doesnt-take-shortest-way
        let delta = ((((toAng - fromAng) % 360) + 540) % 360) - 180;
        // if(toAng != this.lastAng) {
        //     this.lastAng = toAng;
        //     console.log("From: " + fromAng + ", To: " + toAng + ", Delta: " + delta + ", Return: " + (fromAng + delta));
        // }
        return fromAng + delta;
    }

    /**
     * Get gyro permission (not used for now, but keep here in the code for the record - maybe Safari will soon catch up..?)
     * Obs: Currently Safari does not support gyroscope permission (it will ask every time app is reloaded)
     * https://developer.mozilla.org/en-US/docs/Web/API/Permissions_API
     * Second issue is that navigator.permissions is overloaded by native app, so getting the permission does not really make sense for now
     */
    private async getPermissions(): Promise<PermissionState> {
        try {
            const permissions = await navigator.permissions?.query({ name: 'gyroscope' as PermissionName });
            return permissions?.state || 'prompt';
        }
        catch {
            return 'prompt';
        }
    }
}
